"use client";
import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { Link } from "@/navigation";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";
import { getBlogList } from "@/lib/api/blog";
import type { Blog } from "@/lib/@types/api/blog";

interface ApplicationCardProps {
	category: {
		id: string;
		name: string;
		slug: string;
		image: string;
	};
	index: number;
	isLeft: boolean; // 是否在左侧
	parentCategorySlug?: string; // 当前二级分类的 slug
}

const ApplicationCard = ({ category, index, isLeft, parentCategorySlug }: ApplicationCardProps) => {
	const locale = useLocale();
	const [blogs, setBlogs] = useState<Blog.BlogListItem[]>([]);
	const [isLoadingBlogs, setIsLoadingBlogs] = useState<boolean>(true);
	const loadedCategoriesRef = useRef<Set<string>>(new Set());
	// gif 地址：可放 public/image/plsyy/ 或 public/image/plsyy/applications/ 下，文件名 {slug}.gif
	const gifUrl = `/image/plsyy/applications/${category.slug}.gif`;

	const hasOptions = blogs.length > 0;
	
	// 计算需要的列数和行数
	const maxRows = blogs.length <= 8 ? 3 : 4;
	const columnCount = Math.ceil(blogs.length / maxRows);
	// 限制列数范围：最少2列，最多6列
	const gridCols = Math.max(2, Math.min(columnCount, 6));
	
	// 计算实际需要的行数（不超过maxRows）
	const actualRows = Math.min(Math.ceil(blogs.length / gridCols), maxRows);
	
	// 获取该分类下的所有博客
	useEffect(() => {
		const loadBlogs = async () => {
			if (!category.slug) {
				setIsLoadingBlogs(false);
				return;
			}
			
			// 如果已经加载过这个分类，直接返回
			if (loadedCategoriesRef.current.has(category.slug)) {
				return;
			}
			
			loadedCategoriesRef.current.add(category.slug);
			setIsLoadingBlogs(true);
			
			try {
				// 获取当前三级分类下的所有博客
				const blogListRes = await getBlogList({
					lang_code: { lang_code: locale },
					page: 1,
					limit: 100,
					cls_slug_in: [{ slug: category.slug }],
				});
				
				if (blogListRes?.detail?.blog_list) {
					setBlogs(blogListRes.detail.blog_list);
				}
			} catch (error) {
				setBlogs([]);
			} finally {
				setIsLoadingBlogs(false);
			}
		};
		
		loadBlogs();
	}, [category.slug, locale]);

	// hover 时显示 gif；用 key 让每次进入重新挂载 img，gif 从头播放
	const [showGifDesktop, setShowGifDesktop] = useState(false);
	const [showGifMobile, setShowGifMobile] = useState(false);
	const [gifKeyDesktop, setGifKeyDesktop] = useState(0);
	const [gifKeyMobile, setGifKeyMobile] = useState(0);
	const handleImageMouseEnter = () => {
		setShowGifDesktop(true);
		setGifKeyDesktop((k) => k + 1);
	};
	const handleImageMouseLeave = () => setShowGifDesktop(false);
	const handleMobileImageMouseEnter = () => {
		setShowGifMobile(true);
		setGifKeyMobile((k) => k + 1);
	};
	const handleMobileImageLeave = () => setShowGifMobile(false);
	const handleMobileTouchStart = () => {
		setShowGifMobile(true);
		setGifKeyMobile((k) => k + 1);
	};
	const handleMobileTouchEnd = () => setShowGifMobile(false);

	return (
		<motion.div
			initial={{ opacity: 0, y: 30 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5, delay: index * 0.1 }}
			className="group"
		>
			{/* Web端布局 - 左右布局 */}
			<div
				className={`hidden md:flex relative w-full h-[350px] sm:h-[450px] md:h-[550px] lg:h-[560px] transition-all duration-300 overflow-visible ${
					isLeft ? "flex-row" : "flex-row-reverse"
				}`}
			>
				{/* 名称区域 - 占据23% */}
				<div className={`w-[23%] flex flex-col ${isLeft ? "items-start" : "items-end"} relative bg-cover bg-center bg-no-repeat transition-all duration-300 overflow-visible`}>
					
					{/* 标题 - 在白色div上方 */}
					<h2 className={`relative z-10 mt-[15%] mb-4 text-3xl sm:text-3xl md:text-[32px] lg:text-[42px] font-bold text-mainColor transition-colors duration-300 ${isLeft ? "text-left pr-5 pl-0" : "text-right pl-5 pr-0"}`}>
						{category.name}
					</h2>
					
					{/* 白色div，在标题下方，托着标题 */}
					{hasOptions && (
						<div 
							className={`absolute ${isLeft ? "left-0" : "right-0"} bottom-0 bg-white z-20 p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10 shadow-lg group/whiteCard`}
							style={{
								transform: isLeft ? 'translateX(0) translateY(20%)' : 'translateX(0) translateY(20%)',
								height: 'fit-content',
								minWidth: `${Math.max(280, gridCols * 120)}px`,
								width: 'max-content',
								maxWidth: '85vw',
							}}
						>
							<div className="bg-white/10 w-full h-full absolute inset-0"></div>
							{/* 默认SVG */}
							<img 
								src="/image/plsyy/logo-bg.svg" 
								alt="Logo" 
								className="absolute inset-0 w-full h-full object-contain m-auto transition-opacity duration-300 group-hover/whiteCard:opacity-0"
							/>
							{/* hover时显示的SVG */}
							<img 
								src="/image/plsyy/logo-bg-hover.svg" 
								alt="Logo" 
								className="absolute inset-0 w-full h-full object-contain m-auto opacity-0 group-hover/whiteCard:opacity-100 transition-opacity duration-300"
							/>
							
							{/* 选项列表 */}
							<div className="relative w-full flex flex-col justify-center p-3 sm:p-4 md:p-5 lg:p-6 z-10">
								<div 
									className={`grid gap-3 md:gap-4 lg:gap-5 xl:gap-6 text-xs sm:text-sm md:text-base lg:text-lg text-gray-800`}
									style={{
										gridTemplateColumns: `repeat(${gridCols}, minmax(min-content, 1fr))`,
										gridAutoRows: 'auto',
									}}
								>
									{blogs.map((blog, idx) => (
												<Link 
											key={blog.blog_slug || idx} 
											href={`/blog/${blog.blog_slug}`} 
													className="flex items-center text-black hover:text-mainColor transition-colors duration-200 whitespace-nowrap min-w-0 overflow-hidden"
													style={{ width: '100%' }}
												>
											<span className="mr-1 sm:mr-2 text-black text-xl font-bold whitespace-nowrap flex-shrink-0">&gt;</span>
											<span className="whitespace-nowrap flex-shrink-0">
												{blog.blog_title || ""}
											</span>
												</Link>
									))}
								</div>
							</div>
						</div>
					)}
				</div>
				
				{/* 图片区域 - 占据77%，hover 时 gif 淡入 + 静态图微缩放 */}
				<div
					className="w-[77%] relative bg-gray-100 overflow-hidden group/img"
					onMouseEnter={handleImageMouseEnter}
					onMouseLeave={handleImageMouseLeave}
				>
					<div className="relative w-full h-full overflow-hidden">
						{category.image ? (
							<>
								<Image
									src={category.image.startsWith('http') ? category.image : category.image.startsWith('/') ? category.image : `/${category.image}`}
									alt={category.name}
									fill
									className="object-cover scale-110 group-hover/img:scale-125 transition-transform duration-500 ease-out"
									sizes="(max-width: 640px) 75vw, 75vw"
									unoptimized={category.image.startsWith('http') ? false : true}
								/>
								{/* 仅 hover 时挂载 GIF，用 key+缓存破坏参数强制每次从头播放 */}
								{showGifDesktop && (
									<img
										key={gifKeyDesktop}
										src={`${gifUrl}?t=${gifKeyDesktop}`}
										alt={category.name}
										className="absolute inset-0 w-full h-full object-cover z-10 pointer-events-none"
									/>
								)}
							</>
						) : (
							<div className="w-full h-full flex items-center justify-center bg-gray-200">
								<span className="text-gray-400"></span>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* 手机端布局 - 竖向卡片 */}
			<div className="md:hidden flex flex-col w-full bg-white shadow-lg rounded-lg overflow-hidden">
				{/* 图片区域，触摸/长按时播放 slug.gif */}
				<div
					className="relative w-full h-[250px] bg-gray-100 overflow-hidden group/img"
					onMouseEnter={handleMobileImageMouseEnter}
					onMouseLeave={handleMobileImageLeave}
					onTouchStart={handleMobileTouchStart}
					onTouchEnd={handleMobileTouchEnd}
				>
					{category.image ? (
						<>
							<Image
								src={category.image.startsWith('http') ? category.image : category.image.startsWith('/') ? category.image : `/${category.image}`}
								alt={category.name}
								fill
								className="object-cover scale-100 group-hover/img:scale-110 transition-transform duration-500 ease-out"
								sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
								unoptimized={category.image.startsWith('http') ? false : true}
							/>
							{/* 仅显示时挂载 GIF，用 key+缓存破坏参数强制每次从头播放 */}
							{showGifMobile && (
								<img
									key={gifKeyMobile}
									src={`${gifUrl}?t=${gifKeyMobile}`}
									alt={category.name}
									className="absolute inset-0 w-full h-full object-cover z-10 pointer-events-none"
								/>
							)}
						</>
					) : (
						<div className="w-full h-full flex items-center justify-center bg-gray-200">
							<span className="text-gray-400"></span>
						</div>
					)}
				</div>
				
				{/* 内容区域 */}
				<div className="p-4 sm:p-5">
					{/* 选项列表 */}
					{hasOptions && (
						<div className="relative bg-white rounded-lg p-4 shadow-sm border border-gray-100 group/whiteCard">
							<div className="bg-white/10 w-full h-full absolute inset-0 rounded-lg"></div>
							{/* 默认SVG */}
							<img 
								src="/image/plsyy/logo-bg.svg" 
								alt="Logo" 
								className="absolute inset-0 w-full h-full object-contain m-auto transition-opacity duration-300 group-hover/whiteCard:opacity-0 rounded-lg"
							/>
							{/* hover时显示的SVG */}
							<img 
								src="/image/plsyy/logo-bg-hover.svg" 
								alt="Logo" 
								className="absolute inset-0 w-full h-full object-contain m-auto opacity-0 group-hover/whiteCard:opacity-100 transition-opacity duration-300 rounded-lg"
							/>
							
							{/* 选项列表 - 手机端单列显示 */}
							<div className="relative w-full flex flex-col justify-center p-3 z-10 min-h-[120px] max-md:p-0">
								<div className="grid grid-cols-1 gap-3 text-sm text-gray-800">
									{blogs.map((blog, idx) => (
												<Link 
											key={blog.blog_slug || idx} 
											href={`/blog/${blog.blog_slug}`} 
													className="flex items-start text-black hover:text-mainColor transition-colors duration-200"
												>
											<span className="mr-2 text-black text-lg font-bold whitespace-nowrap flex-shrink-0 mt-[-2px]">&gt;</span>
											<span className="break-words">
												{blog.blog_title || ""}
											</span>
												</Link>
									))}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</motion.div>
	);
};

export default ApplicationCard;

